/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import noop from 'lodash/noop';

/**
 * Čistič místa na disku.
 * Snaží se zajistit dostatek volného místa pro nový obsah.
 * @implements {EventBus}
 */
class Cleaner {
  /**
   * @param {AssetDownloader} assetDownloader
   * @param {BasePathResolver} basePathResolver Mapovač logických cesta na fyzické.
   * @param {Store} primaryStore Hlavní datový sklad ve kterém je uložen přehrávaný obsah.
   * @param {Session} session Sezení s nastavením zařízení.
   */
  constructor(assetDownloader, basePathResolver, primaryStore, session) {
    this.assetDownloader = assetDownloader;
    this.basePathResolver = basePathResolver;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.primaryStore = primaryStore;
    this.session = session;
    this.temporaryExtension = 'tmp';
  }

  /**
   * Vyčistí místo na disku pro nový obsah.
   * @param {FileSystemPath} logicalPath Cesta ve které se provést čištění.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise.<CleanerInfo>} Informace o vyčištěných datových modelech.
   */
  async clean(logicalPath, contentInfo) {
    const cleanerInfo = {
      assetArray: [],
      mediumArray: [],
      filesArray: []
    };
    const { primaryStore } = this;
    // Ty media, co mají být aktuálně uložená
    const expectedMediaId = contentInfo.mediumArray.map((medium) => medium.id);
    const { mediaToRemove, assetsWithoutMedium } = await primaryStore
      .findAll('medium', {}, { with: ['asset'] })
      .then(async (media) => {
        const allAssetsId = media
          .map((m) => m.assetId)
          .filter((assetId) => Boolean(assetId));
        // Přidání assetů z nového contentInfo, které se již stáhly, ale ještě nebyly zapsány do hlavního úložiště.
        const newlyDownloadedAssets = contentInfo.mediumArray
          .map((m) => m.assetId)
          .filter((id) => Boolean(id));
        allAssetsId.push(...newlyDownloadedAssets);
        return {
          // Ponech všechny media, která nejsou v očekávaném seznamu medií. Tj. zůstanou pouze media, která mohou být odstraněny.
          mediaToRemove: media.filter((m) => !expectedMediaId.includes(m.id)),
          assetsWithoutMedium: await primaryStore
            .findAll('asset')
            .then((assets) => assets.filter((a) => !allAssetsId.includes(a.id)))
        };
      });

    if (mediaToRemove.length) {
      // Požadavek na zastavení aktuálně přehrávaného obsahu.
      this.emitter.emit('content', {
        contentTasks: [],
        firmwareRelease: null,
        layerArray: [],
        layoutArray: [],
        mediumArray: [],
        playlistArray: [],
        powerTasks: [],
        // Zachová se obsah aktuální session.
        // Při zrušení hodnot by mohlo dojít ke zbytečné změně rotace obrazovky apod.
        session: this.session,
        store: primaryStore,
        taskArray: [],
        timerArray: [],
        trackArray: []
      });
    }

    return (
      new Promise((resolve) =>
        resolve(
          this.basePathResolver
            .toPhysicalPath(logicalPath)
            .then((physicalPath) =>
              // Vylistuje a smaže všechny soubory bez vazby na asset
              this.assetDownloader
                .listFile(physicalPath)
                .then((files) => {
                  // Pole cest všechny assetů z localstorage
                  const assetsPath = primaryStore
                    .filter('asset')
                    .map((a) => a.path)
                    .filter((p) => Boolean(p));

                  return files.filter(
                    (f) => !assetsPath.includes(f.entry.getPath().getValue())
                  );
                })
                .then((files) =>
                  // Smažou se všechny soubory, které nemají vazbu na asset
                  Promise.all(
                    files.map((f) =>
                      f.entry
                        .remove({ recursive: true })
                        .then(() => ({ id: f.id }))
                        .catch(noop)
                    )
                  )
                )
            )
        )
      )
        // Informace o smazanych souborech se ulozi
        .then((files) => cleanerInfo.filesArray.push(...files))
        .catch((err) => this.emitter.emit('error', err))
        .then(() =>
          Promise.all([
            // Odstraní media společně s assety, které nejsou potřeba podle contentInfo.
            ...mediaToRemove.map((m) => {
              const { asset } = m;
              const assetId = asset?.id;
              return this.assetDownloader
                .destroy(assetId)
                .then(() =>
                  primaryStore
                    .update('medium', m.id, { assetId: null })
                    .then(() => {
                      cleanerInfo.assetArray.push({ id: assetId });
                      cleanerInfo.mediumArray.push({ assetId, id: m.id });
                    })
                )
                .catch((err) => this.emitter.emit('error', err));
            }),
            // Odstraní assety, které nemají vazbu na medium.
            ...assetsWithoutMedium.map((a) =>
              this.assetDownloader
                .destroy(a.id)
                .then(() => cleanerInfo.assetArray.push({ id: a.id }))
                .catch((err) => this.emitter.emit('error', err))
            )
          ]).then(() => cleanerInfo)
        )
    );
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }
}

/**
 * Nový obsah zařízení k zobrazení.
 * Používá se pro přerušení přehrávání aktuálního obsahu, aby mohl být vyčištěn.
 * @event Cleaner#content
 * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
 */

/**
 * Chybová událost.
 * @event Cleaner#error
 * @param {Error} error Chybové hlášení.
 */

export default Cleaner;
